import { Button, DatePicker, Input, Select } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useGetUsersQuery } from '@/state/slices/users';
import { CollapsibleSection } from '@/shared/ui/CollapsibleSection';
import { ActionTypeChoices, ObjectTypeChoices } from '@/models/logItem';
import styles from './AuditFilterRail.module.css';

const ACTION_TYPE_OPTIONS = Object.values(ActionTypeChoices).map((v) => ({
  value: v,
  label: v,
}));

const OBJECT_TYPE_OPTIONS = Object.values(ObjectTypeChoices).map((v) => ({
  value: v,
  label: v,
}));

interface AuditFilterRailProps {
  dateRange: [string, string];
  onDateRangeChange: (range: [string, string]) => void;
  userId: string | undefined;
  onUserChange: (id: string | undefined) => void;
  actionTypes: string[];
  onActionTypesChange: (v: string[]) => void;
  objectType: string | undefined;
  onObjectTypeChange: (v: string | undefined) => void;
  objectId: string | undefined;
  onObjectIdChange: (v: string | undefined) => void;
  onExportCsv: () => void;
  isExporting?: boolean;
}

export default function AuditFilterRail({
  dateRange,
  onDateRangeChange,
  userId,
  onUserChange,
  actionTypes,
  onActionTypesChange,
  objectType,
  onObjectTypeChange,
  objectId,
  onObjectIdChange,
  onExportCsv,
  isExporting,
}: AuditFilterRailProps) {
  const { data: usersData } = useGetUsersQuery({ page: 1, size: 200 });

  const userOptions = useMemo(
    () =>
      usersData?.results.map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`,
      })) ?? [],
    [usersData]
  );

  const handleRangePicker = (
    _dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    if (dateStrings[0] && dateStrings[1]) {
      onDateRangeChange([
        dayjs(dateStrings[0]).startOf('day').toISOString(),
        dayjs(dateStrings[1]).endOf('day').toISOString(),
      ]);
    }
  };

  const objectCount = (objectType ? 1 : 0) + (objectId ? 1 : 0);

  return (
    <div className={styles.filterRail}>
      {/* ── Date Range ────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Date Range</span>
          <DatePicker.RangePicker
            className={styles.fullWidth}
            value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
            onChange={handleRangePicker}
          />
        </div>
      </div>

      {/* ── User ─────────────────────────────── */}
      <div className={styles.section}>
        <div className={styles.filterGroup}>
          <label htmlFor="audit-user" className={styles.filterLabel}>
            User
          </label>
          <Select
            id="audit-user"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All users"
            value={userId}
            onChange={(v) => onUserChange(v ?? undefined)}
            options={userOptions}
            className={styles.fullWidth}
          />
        </div>
      </div>

      {/* ── Action Type ───────────────────────── */}
      <div className={styles.section}>
        <div className={styles.filterGroup}>
          <label htmlFor="audit-action-type" className={styles.filterLabel}>
            Action Type
          </label>
          <Select
            id="audit-action-type"
            mode="multiple"
            allowClear
            placeholder="All actions"
            value={actionTypes}
            onChange={onActionTypesChange}
            options={ACTION_TYPE_OPTIONS}
            className={styles.fullWidth}
          />
        </div>
      </div>

      {/* ── Object Filters ────────────────────── */}
      <CollapsibleSection
        title="Object Filters"
        count={objectCount}
        onClear={() => {
          onObjectTypeChange(undefined);
          onObjectIdChange(undefined);
        }}
      >
        <div className={styles.filterGroup}>
          <label htmlFor="audit-object-type" className={styles.filterLabel}>
            Object Type
          </label>
          <Select
            id="audit-object-type"
            allowClear
            placeholder="Any type"
            value={objectType}
            onChange={(v) => onObjectTypeChange(v ?? undefined)}
            options={OBJECT_TYPE_OPTIONS}
            className={styles.fullWidth}
          />
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="audit-object-id" className={styles.filterLabel}>
            Object ID
          </label>
          <Input
            id="audit-object-id"
            allowClear
            placeholder="UUID"
            value={objectId ?? ''}
            onChange={(e) => onObjectIdChange(e.target.value || undefined)}
          />
        </div>
      </CollapsibleSection>

      {/* ── Actions ──────────────────────────── */}
      <div className={styles.actions}>
        <span className={styles.filterLabel}>Actions</span>
        <Button
          block
          icon={<DownloadOutlined />}
          onClick={onExportCsv}
          loading={isExporting}
          disabled={isExporting}
        >
          Export CSV
        </Button>
      </div>
    </div>
  );
}
